import { usePostHog, PostHog } from "@posthog/react";
import { useCallback, useEffect, useRef } from "react";

let posthogInstance: PostHog | null = null;

export const ENABLE_MENTOR_SCHEDULE_FLAG = "enable-mentor-schedule";

export const setPostHogInstance = (instance: PostHog) => {
  posthogInstance = instance;
};

export const isMentorScheduleEnabled = () =>
  import.meta.env.DEV ||
  posthogInstance?.isFeatureEnabled(ENABLE_MENTOR_SCHEDULE_FLAG) === true;

export const PostHogEvents = {
  // Teen wall (D7-20)
  MENTOR_PROFILE_VIEWED: "mentor_profile_viewed",

  // Messaging
  TEEN_MESSAGE_RECEIVED: "teen_message_received",
  MENTOR_MESSAGE_SENT: "mentor_message_sent",
  OPENED_CHAT: "opened_chat",

  // Voice Note Transcript
  VOICE_NOTE_TRANSCRIPT_VIEWED: "voice_note_transcript_viewed",
  VOICE_NOTE_TRANSCRIPT_UNAVAILABLE: "voice_note_transcript_unavailable",

  // Calls / Sessions
  CALL_STARTED: "call_started",
  CALL_ENDED: "call_ended",
  SESSION_SCHEDULED: "session_scheduled",

  // Suggested Messages
  SUGGESTED_MESSAGE_INSERTED: "suggested_message_inserted",
  SUGGESTED_MESSAGE_SENT: "suggested_message_sent",
  SUGGESTED_MESSAGE_EDITED: "suggested_message_edited",

  // AI Copilot
  COPILOT_CHAT_STARTED: "copilot_chat_started",
  COPILOT_RESPONSE_INSERTED: "copilot_response_inserted",
  COPILOT_RESPONSE_ADDED_TO_NOTE: "copilot_response_added_to_note",

  // Documents
  DOCUMENT_CREATED: "document_created",
  DOCUMENT_EDITED: "document_edited",

  // Todos
  TODO_VIEWED: "todo_viewed",
  TODO_CLICKED: "todo_clicked",

  // Navigation
  PAGE_HOME: "page_home",
  PAGE_CHAT: "page_chat",
  PAGE_UPCOMING: "page_upcoming",
  PAGE_SETTINGS: "page_settings",
  PAGE_NEW_TEENS: "page_new_teens",
  PAGE_TRAINING: "page_training",
  PAGE_TRAINING_SESSION: "page_training_session",
  PAGE_TRAINING_REVIEW: "page_training_review",
  PAGE_TRAINING_REVIEW_DETAIL: "page_training_review_detail",

  // Mentor Performance Overview
  MENTOR_DASHBOARD_MENTOR_PROFILE_CLICKED:
    "mentor_dashboard_mentor_profile_clicked",
  MENTOR_DASHBOARD_QUICK_MESSAGE_CLICKED:
    "mentor_dashboard_quick_message_clicked",
  MENTOR_DASHBOARD_TABLE_SORTED: "mentor_dashboard_table_sorted",
  MENTOR_DASHBOARD_TIME_PERIOD_CHANGED: "mentor_dashboard_time_period_changed",
  MENTOR_DASHBOARD_SUPPORT_CHAT_MESSAGE_VIEWED:
    "mentor_dashboard_support_chat_message_viewed",
  MENTOR_DASHBOARD_SEARCH_USED: "mentor_dashboard_search_used",
  MENTOR_DASHBOARD_LEAD_MENTOR_FILTER_CHANGED:
    "mentor_dashboard_lead_mentor_filter_changed",

  // Training Simulator
  SIMULATED_ESCALATION_CLICKED: "simulated_escalation_clicked",
  SIMULATOR_LEAD_MENTOR_MESSAGE_MODAL_OPEN:
    "simulator_lead_mentor_message_modal_open",
  SIMULATOR_REVIEW_MESSAGE_MENTOR_MODAL_OPEN:
    "simulator_review_message_mentor_modal_open",
  SIMULATOR_MESSAGE_SENT: "simulator_message_sent",
  SIMULATOR_ESCALATE_CLICK: "simulator_escalate_click",
  SIMULATOR_LM_AI_FEEDBACK_RATE: "simulator_lm_ai_feedback_rate",
  SIMULATOR_LM_FEEDBACK_SUBMIT: "simulator_lm_feedback_submit",
  SIMULATOR_NAV_CLICK: "simulator_nav_click",
  SIMULATOR_START_SESSION_CTA_CLICK: "simulator_start_session_cta_click",
  SIMULATOR_BACK_TO_TRAINING_CLICK: "simulator_back_to_training_home_click",
  SIMULATOR_WELCOME_MODAL_DISMISS: "simulator_welcome_modal_dismiss",
  SIMULATOR_LEVEL_CONTEXT_MODAL_DISMISS:
    "simulator_level_context_modal_dismiss",
  SIMULATOR_ESCALATE_MODAL_DISMISS: "simulator_escalate_modal_dismiss",
  SIMULATOR_LEAD_MENTOR_MESSAGE_SENT: "simulator_lead_mentor_message_sent",
  SIMULATOR_LM_SECTION_TOGGLE: "simulator_lm_section_toggle",
  SIMULATOR_LM_REVIEW_TRAINING_CLICK: "simulator_lm_review_training_click",
  SIMULATOR_LM_MODULE_SELECT: "simulator_lm_module_select",
  SIMULATOR_LM_LEVEL_SELECT: "simulator_lm_level_select",
  SIMULATOR_LEVEL_START: "simulator_level_start",
  SIMULATOR_LEVEL_RETRY: "simulator_level_retry",
  SIMULATOR_LEVEL_EXIT: "simulator_level_exit",
  SIMULATOR_MODULE_CONTINUE_CLICK: "simulator_module_continue_click",
  SIMULATOR_LM_SEND_MESSAGE_CLICK: "simulator_lm_send_message_click",

  // Hub - Publish/Share
  HUB_MOMENT_SHARED: "mentor_hub_moment_shared",
  HUB_NOTE_SHARED: "mentor_hub_note_shared",

  // Hub - Comments
  HUB_MOMENT_COMMENT_SENT: "mentor_hub_moment_comment_sent",
  HUB_NOTE_COMMENT_SENT: "mentor_hub_note_comment_sent",

  // Hub - Reactions
  HUB_MOMENT_REACTED: "mentor_hub_moment_reacted",
  HUB_NOTE_REACTED: "mentor_hub_note_reacted",

  // Hub - Read State
  HUB_MOMENT_MARKED_READ: "mentor_hub_moment_marked_read",
  HUB_MOMENT_MARKED_UNREAD: "mentor_hub_moment_marked_unread",
  HUB_NOTE_MARKED_READ: "mentor_hub_note_marked_read",
  HUB_NOTE_MARKED_UNREAD: "mentor_hub_note_marked_unread",

  // Hub - Open/View
  HUB_MOMENT_OPENED: "mentor_hub_moment_opened",
  HUB_NOTE_OPENED: "mentor_hub_note_opened",

  // Magic Moment - Day 1
  DAY1_BANNER_VIEW: "mentor_chat_day1_banner_view",
  DAY1_BANNER_TOGGLE: "mentor_chat_day1_banner_toggle",
  DAY1_TIP_CLICK: "mentor_chat_day1_tip_click",
  DAY1_BANNER_EXPIRE: "mentor_chat_day1_banner_expire",

  // Magic Moment - Day 2
  DAY2_BADGE_VIEW: "mentor_chat_day2_badge_view",
  DAY2_MODAL_VIEW: "mentor_chat_day2_modal_view",
  DAY2_MODAL_DISMISS: "mentor_chat_day2_modal_dismiss",
  DAY2_BANNER_VIEW: "mentor_chat_day2_banner_view",
  DAY2_BANNER_TOGGLE: "mentor_chat_day2_banner_toggle",
  DAY2_ACTION_CLICK: "mentor_chat_day2_action_click",
  DAY2_MAGIC_MOMENT_VIEW: "mentor_chat_day2_magic_moment_view",
  DAY2_TIPS_VIEW: "mentor_chat_day2_tips_view",
  DAY2_TIPS_BACK: "mentor_chat_day2_tips_back",
  DAY2_MAGIC_MOMENT_BACK: "mentor_chat_day2_magic_moment_back",
  DAY2_BANNER_EXPIRE: "mentor_chat_day2_banner_expire",
  DAY2_BANNER_DISMISS: "mentor_chat_day2_banner_dismiss",
  DAY1_BANNER_DISMISS: "mentor_chat_day1_banner_dismiss",

  // Reactivation
  REACTIVATION_BANNER_VIEW: "mentor_chat_reactivation_banner_view",
  REACTIVATION_BANNER_TOGGLE: "mentor_chat_reactivation_banner_toggle",

  // Momentum Alert
  MOMENTUM_ALERT_BANNER_VIEW: "mentor_chat_momentum_alert_banner_view",
  MOMENTUM_ALERT_BANNER_TOGGLE: "mentor_chat_momentum_alert_banner_toggle",
  MOMENTUM_ALERT_ACTION_CLICK: "mentor_chat_momentum_alert_action_click",

  // Content Takedown
  MENTOR_CHAT_TAKEDOWN_VIEW: "mentor:chat_takedown_view",
  MENTOR_CHAT_TAKEDOWN_REVERSE: "mentor:chat_takedown_reverse",

  // User Reports
  SUPERVISOR_REPORT_VIEW: "supervisor:report_view",

  // Ops Chat Dashboard
  OPS_CHAT_DASHBOARD_VIEW: "ops_chat_dashboard_view",
  OPS_CHAT_SEGMENT_TOGGLE: "ops_chat_segment_toggle",
  OPS_CHAT_CATEGORY_EXPAND: "ops_chat_category_expand",
  OPS_CHAT_THREAD_OPEN: "ops_chat_thread_open",
  OPS_CHAT_MESSAGE_SEND: "ops_chat_message_send",
  OPS_CHAT_NEW_CHAT_OPEN: "ops_chat_new_chat_open",
  OPS_CHAT_NEW_CHAT_DROPDOWN_VIEW: "ops_chat_new_chat_dropdown_view",
  OPS_CHAT_NEW_CHAT_DROPDOWN_FILTER: "ops_chat_new_chat_dropdown_filter",
  OPS_CHAT_NEW_CHAT_RECIPIENT_ADD: "ops_chat_new_chat_recipient_add",
  OPS_CHAT_NEW_CHAT_RECIPIENT_REMOVE: "ops_chat_new_chat_recipient_remove",
  OPS_CHAT_NEW_CHAT_HISTORY_RENDER: "ops_chat_new_chat_history_render",
  OPS_CHAT_NEW_CHAT_DISMISS: "ops_chat_new_chat_dismiss",
  OPS_CHAT_NEW_CHAT_SEND: "ops_chat_new_chat_send",
  OPS_CHAT_FAVORITE_TOGGLE: "ops_chat_favorite_toggle",

  // Notice Layer
  NOTICE_WORKSPACE_SWITCH: "notice_workspace_switch",
  NOTICE_FILTER_APPLY: "notice_filter_apply",
  NOTICE_SEARCH_OPEN: "notice_search_open",
  NOTICE_SEARCH_USE: "notice_search_use",
  NOTICE_BADGE_VIEW: "notice_badge_view",
  NOTICE_ROW_OPEN: "notice_row_open",

  // Attention Layer
  ATTENTION_BANNER_VIEW: "attention_banner_view",
  ATTENTION_BANNER_CLICK: "attention_banner_click",
  ATTENTION_BANNER_DISMISS: "attention_banner_dismiss",
  ATTENTION_BANNER_COLLAPSE: "attention_banner_collapse",
  ATTENTION_BANNER_EXPAND: "attention_banner_expand",
  ATTENTION_COMPOSER_TOOLBAR_ACTION: "attention_composer_toolbar_action",
  ATTENTION_MESSAGE_SEND: "attention_message_send",
  ATTENTION_VOICE_RECORD_SEND: "attention_voice_record_send",
  ATTENTION_VOICE_RECORD_CANCEL: "attention_voice_record_cancel",
  ATTENTION_SUGGESTION_OPEN: "attention_suggestion_open",
  ATTENTION_SUGGESTION_INSERT: "attention_suggestion_insert",
  ATTENTION_SUGGESTION_REGENERATE: "attention_suggestion_regenerate",
  ATTENTION_SUGGESTION_DISMISS: "attention_suggestion_dismiss",
  ATTENTION_HEADER_ACTION: "attention_header_action",

  // Action Layer
  ACTION_TAB_SWITCH: "action_tab_switch",
  ACTION_COLLAPSE_TOGGLE: "action_collapse_toggle",
  ACTION_SUGGESTION_VIEW: "action_suggestion_view",
  ACTION_SUGGESTION_ACCEPT: "action_suggestion_accept",
  ACTION_SUGGESTION_DISMISS: "action_suggestion_dismiss",
  ACTION_SUGGESTION_RESTORE: "action_suggestion_restore",
  ACTION_NOTE_ADD: "action_note_add",
  ACTION_NOTE_EDIT: "action_note_edit",
  ACTION_NOTE_DELETE: "action_note_delete",
  ACTION_NOTES_SEARCH: "action_notes_search",
  ACTION_TOOL_SELECT: "action_tool_select",
  ACTION_HISTORY_VIEW: "action_history_view",
  ACTION_HISTORY_ENTRY_SELECT: "action_history_entry_select",
  ACTION_HISTORY_DAY_SELECT: "action_history_day_select",
  ACTION_AI_OPEN: "action_ai_open",
  ACTION_AI_QUERY_SUBMIT: "action_ai_query_submit",
  ACTION_AI_CHIP_CLICK: "action_ai_chip_click",

  // Mentor profile onboarding
  MENTOR_PROFILE_ONBOARDING_EMAIL_CLICKED:
    "mentor_profile_onboarding_email_clicked",
  MENTOR_PROFILE_ONBOARDING_STARTED: "mentor_profile_onboarding_started",
  MENTOR_PROFILE_PASSWORD_CREATED: "mentor_profile_password_created",
  MENTOR_PROFILE_CONFIRM_INFO_FIELD_EDITED:
    "mentor_profile_confirm_info_field_edited",
  MENTOR_PROFILE_CONFIRM_INFO_COMPLETED:
    "mentor_profile_confirm_info_completed",
  MENTOR_PROFILE_PHOTO_UPLOADED: "mentor_profile_photo_uploaded",
  MENTOR_PROFILE_PHOTO_PROCESSED: "mentor_profile_photo_processed",
  MENTOR_PROFILE_PHOTO_CONFIRMED: "mentor_profile_photo_confirmed",
  MENTOR_PROFILE_DISPLAY_NAME_COMPLETED:
    "mentor_profile_display_name_completed",
  MENTOR_PROFILE_BIO_COMPLETED: "mentor_profile_bio_completed",
  MENTOR_PROFILE_SPOTIFY_LINK_ADDED: "mentor_profile_spotify_link_added",
  MENTOR_PROFILE_AUDIO_INTRO_CONFIRMED: "mentor_profile_audio_intro_confirmed",
  MENTOR_PROFILE_VIBE_PHOTOS_CONFIRMED: "mentor_vibe_photos_confirmed",
  MENTOR_PROFILE_INTEREST_PHOTOS_CONFIRMED: "mentor_interest_photos_confirmed",
  MENTOR_PROFILE_SUBMITTED: "mentor_profile_submitted",
  MENTOR_PROFILE_REVISION_SUBMITTED: "mentor_revision_submitted",
  MENTOR_PROFILE_REVISION_FEEDBACK_POPUP_VIEWED:
    "mentor_revision_feedback_popup_viewed",
  MENTOR_PROFILE_APPROVED: "mentor_profile_approved",
  MENTOR_PROFILE_REJECTED: "mentor_profile_rejected",
  MENTOR_PROFILE_ONBOARDING_FLOW_COMPLETED: "mentor_onboarding_flow_completed",
  LEAD_MENTOR_REVIEW_OPENED: "lead_mentor_review_opened",
  LEAD_MENTOR_CRITERION_COMMENT_ADDED: "lead_mentor_criterion_comment_added",

  // User Records / Self-View (MO-4.1)
  USER_RECORD_SAVED: "user_record_saved",
  USER_RECORD_ACTIVATION_CHANGE: "user_record_activation_change",
  USER_RECORD_MESSAGE_CLICKED: "user_record_message_clicked",
  SELF_RECORD_CLICKED: "self_record_clicked",
  SELF_RECORD_SAVED: "self_record_saved",
  SELF_RECORD_PROFILE_CHANGE_REQUESTED: "self_record_profile_change_requested",

  // Care page (MO-9.1)
  CARE_PAGE_CHANGE_PRESENTATION_CLICKED:
    "care_page_change_presentation_clicked",
  CARE_PAGE_PRESENTATION_CHANGE_SUBMITTED:
    "care_page_presentation_change_submitted",
  CARE_PAGE_WRITE_CARE_UPDATE_CLICKED: "care_page_write_care_update_clicked",
  CARE_PAGE_CARE_UPDATE_SUBMITTED: "care_page_care_update_submitted",
  CARE_PAGE_REVIEW_PRESENTATION_REVIEW_CLICKED:
    "care_page_review_presentation_review_clicked",
  CARE_PAGE_REVIEW_CHANGE_PRESENTATION_CLICKED:
    "care_page_review_change_presentation_clicked",
  CARE_PAGE_REVIEW_SUBMITTED: "care_page_review_submitted",
  CARE_PAGE_REVIEW_SAVE_DRAFT_CLICKED: "care_page_review_save_draft_clicked",
  CARE_PAGE_REVIEW_DRAFT_RESUMED: "care_page_review_draft_resumed",
  CARE_PAGE_REVIEW_VIEW_AI_NOTES_CLICKED:
    "care_page_review_view_ai_notes_clicked",
  CARE_PAGE_HISTORY_FILTER_CLICKED: "care_page_history_filter_clicked",
  CARE_PAGE_HISTORY_SHOW_15_MORE_CLICKED:
    "care_page_history_show_15_more_clicked",
  CARE_PAGE_HISTORY_ROW_EXPANDED: "care_page_history_row_expanded",
  CARE_PAGE_VIEW_CHAT_CLICKED: "care_page_view_chat_clicked",
  CARE_PAGE_AI_FEEDBACK_SUBMITTED: "care_page_ai_feedback_submitted",

  CARE_REVIEWS_TOGGLE_ENTRIES: "care_reviews_toggle_entries",
  CARE_REVIEWS_OPEN_CARE_PAGE: "care_reviews_open_care_page",
  CARE_REVIEWS_SUBMISSION_UNDO: "care_reviews_submission_undo",

  MENTOR_STORY_EDIT_CTA_CLICKED: "mentor_story_edit_cta_clicked",
  MENTOR_STORY_NEXT_STORY_CLICKED: "mentor_story_next_story_clicked",
  MENTOR_STORY_DRAFT_SAVED: "mentor_story_draft_saved",
  MENTOR_STORY_DRAFT_DELETED: "mentor_story_draft_deleted",
  MENTOR_STORY_SUBMITTED_FOR_REVIEW: "mentor_story_submitted_for_review",
  ADMIN_STORY_REVIEW_OPENED: "admin_story_review_opened",
  ADMIN_STORY_APPROVED_AND_PUBLISHED: "admin_story_approved_and_published",
  ADMIN_STORY_REJECTED_WITH_COMMENTS: "admin_story_rejected_with_comments",
  ADMIN_STORY_ADMIN_DELETED_STORY: "admin_story_admin_deleted_story",
} as const;

export type PostHogEventName =
  (typeof PostHogEvents)[keyof typeof PostHogEvents];

export interface OpenedChatMetadata {
  teen_id?: string;
  teen_name?: string;
  channel_url?: string;
}

export interface CallMetadata {
  teen_id?: string;
  call_id?: string;
  room_id?: string;
}

export interface SessionMetadata {
  teen_id?: string;
  mentorship_id?: string;
  scheduled_time?: string;
}

export interface SuggestedMessageMetadata {
  suggestion_type?: "default" | "specific" | "priority";
  approach?: string;
  text_length?: number;
  was_edited?: boolean;
}

export interface CopilotMetadata {
  mentorship_id?: string;
  response_length?: number;
}

export interface DocumentMetadata {
  mentorship_id?: string;
  has_reminder?: boolean;
  note_id?: string;
}

export interface TodoMetadata {
  notification_type?: string;
  notification_id?: string;
  teen_name?: string;
}

export interface SupervisorReportViewMetadata {
  report_id: string;
  time_to_view_ms: number;
}

export function useTrackEvent() {
  const posthog = usePostHog();

  // Store instance for non-hook usage (like Sendbird handlers)
  if (posthog && !posthogInstance) {
    posthogInstance = posthog;
  }

  const track = useCallback(
    (event: PostHogEventName, properties?: Record<string, unknown>) => {
      if (!posthog) return;
      posthog.capture(event, properties);
    },
    [posthog],
  );

  return { track };
}

export function useTrackPage(
  event: PostHogEventName,
  properties?: Record<string, unknown>,
  options?: { enabled?: boolean },
) {
  const { track } = useTrackEvent();
  const tracked = useRef(false);
  const propertiesRef = useRef(properties);
  propertiesRef.current = properties;
  const enabled = options?.enabled ?? true;

  useEffect(() => {
    if (!enabled || tracked.current) return;
    tracked.current = true;
    track(event, propertiesRef.current);
  }, [enabled, event, track]);
}

export const trackEvent = (
  event: PostHogEventName,
  properties?: Record<string, unknown>,
) => {
  if (posthogInstance) {
    posthogInstance.capture(event, properties);
  } else if (window.posthog) {
    window.posthog.capture(event, properties);
  } else {
    console.warn(`📊 [PostHog] Event not sent - PostHog not available`);
  }
};

export function useSetPersonProperties(
  properties: Record<string, unknown> | undefined,
) {
  const posthog = usePostHog();

  useEffect(() => {
    if (properties && posthog) {
      posthog.people.set(properties);
    }
  }, [properties, posthog]);
}

export function useTrackMentorActiveTeens(activeTeens: number | undefined) {
  const posthog = usePostHog();

  useEffect(() => {
    if (activeTeens !== undefined && posthog) {
      posthog.people.set({ active_teens: activeTeens });
    }
  }, [activeTeens, posthog]);
}
